/**
 * Scene short-code loader.
 *
 * Resolves `?code=XXXX` URL params to a CMS scene by calling `GET /code/{code}`,
 * then rewrites the URL search to `?scene={id}&api={base}` so the existing
 * CMS bootstrap path in `index.ts` takes over.
 *
 * Auth: prefers a logged-in session (refresh-token in localStorage) but falls
 * back to guest login so anyone with a code can view the scene.
 */

import { Code, type AuthTokenResponseDto } from 'trace-viewer/cms';
import { API_BASE_URL, ensureFreshToken, setCatalogToken } from './catalog-auth';
import { apiConfig } from './catalog-shared';
import { showToast } from './toast';

/** Direct fetch (bypasses apiConfig's securityWorker, which expects a refresh-token session). */
async function loginAsGuest(): Promise<string | null> {
    try {
        const res = await fetch(`${API_BASE_URL}/auth/login/guest`, { method: 'POST' });
        if (!res.ok) {
            console.error(`[Sandbox] Guest login failed (${res.status})`);
            return null;
        }
        const data = await res.json() as AuthTokenResponseDto;
        return data.access_token ?? null;
    } catch (err) {
        console.error('[Sandbox] Guest login error:', err);
        return null;
    }
}

async function ensureAuthToken(): Promise<string | null> {
    const existing = await ensureFreshToken();
    if (existing) return existing;

    const guest = await loginAsGuest();
    if (guest) setCatalogToken(guest);
    return guest;
}

/**
 * If the URL has `?code=XXXX`, resolve it to a scene id and rewrite the URL
 * to the standard CMS form. No-op when `?code=` is absent.
 *
 * @returns true if a code was resolved and the URL was rewritten.
 */
export async function resolveCodeParam(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get('code');
    if (!rawCode) return false;

    const code = rawCode.replace(/[-\s]/g, '');
    if (!code) return false;

    if (!API_BASE_URL) {
        console.error('[Sandbox] ?code= requires VITE_API_BASE_URL to be set');
        showToast('Code lookup unavailable (missing API URL)', 'error');
        return false;
    }

    const token = await ensureAuthToken();
    if (!token) {
        console.error('[Sandbox] ?code= could not obtain an auth token');
        showToast(`Could not load "${code}" — sign-in failed`, 'error');
        return false;
    }

    let sceneId: string | undefined;
    try {
        const res = await new Code(apiConfig(token)).codeDetail(code);
        const result = res.data;
        sceneId = result.scene?.id ?? result.project?.scenes?.[0]?.id;
    } catch (err) {
        console.error(`[Sandbox] Code "${code}" lookup failed:`, err);
        showToast(`Could not find scene "${code}"`, 'error');
        return false;
    }

    if (!sceneId) {
        console.error(`[Sandbox] Code "${code}" did not resolve to a scene`);
        showToast(`Code "${code}" did not match any scene`, 'error');
        return false;
    }

    params.delete('code');
    params.set('scene', sceneId);
    params.set('api', API_BASE_URL);
    const newSearch = `?${params.toString()}`;
    window.history.replaceState(null, '', `${window.location.pathname}${newSearch}${window.location.hash}`);
    console.log(`[Sandbox] Code "${code}" resolved to scene ${sceneId}`);
    return true;
}
